import { isViewAsSelected } from "@/store/modules/TargetUserModule";
import { getValue, removeValue, setValue } from "@/utils/SecureLocalStorage";

/**
 * Where a screen keeps the branch (Standort) it filters by, so the choice outlives a page change.
 *
 * While "Sicht auf" is active the choice is the chosen user's, not the viewer's: it goes to its own
 * per-tab copy in session storage, starting empty (so the chosen user's default applies), and is
 * dropped when "Sicht auf" starts or ends. The viewer's own choice in local storage is never touched,
 * so it is back as it was afterwards (ticket 2610052; before, the viewed user's branch leaked into
 * the viewer's own lists).
 *
 * The keys in use: "branchFilter" (Cockpit, Projekte, Alte Projekte, HyDry Geräte) and
 * "AllDevicesBranchFilter" (Alle Geräte and its Cockpit tile).
 */

const VIEW_AS_KEY_PREFIX = "VIEW_AS_";
const BRANCH_FILTER_KEYS = ["branchFilter", "AllDevicesBranchFilter"];

/** The stored branch id under `key`, the "Sicht auf" copy while viewing. */
export function getStoredBranchFilterId(key: string): string | null {
  return isViewAsSelected()
    ? getValue(VIEW_AS_KEY_PREFIX + key) ?? null
    : localStorage.getItem(key);
}

/** Removes the stored branch id under `key` (the "Sicht auf" copy while viewing). */
export function removeStoredBranchFilterId(key: string) {
  if (isViewAsSelected()) {
    removeValue(VIEW_AS_KEY_PREFIX + key);
  } else {
    localStorage.removeItem(key);
  }
}

/** Stores the branch id under `key`, or removes it for "all branches". */
export function setStoredBranchFilterId(key: string, branchId: string | null | undefined) {
  if (!branchId) {
    removeStoredBranchFilterId(key);
    return;
  }

  if (isViewAsSelected()) {
    setValue(VIEW_AS_KEY_PREFIX + key, branchId);
  } else {
    localStorage.setItem(key, branchId);
  }
}

/** Drops the "Sicht auf" copies; runs whenever "Sicht auf" starts or ends. */
export function clearViewAsBranchFilters() {
  BRANCH_FILTER_KEYS.forEach((key) => removeValue(VIEW_AS_KEY_PREFIX + key));
}
